import React, { Suspense, useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, LinkButton, Stack, StatusPill, Typography } from '@lyearn/ui';
import ActionsList from '@/components/Header/components/ActionsList';
import NotificationList from '@/components/Header/components/NotificationList';
import { NotificationDetailsFragment } from '@/components/Header/components/NotificationTray/graphql/fragments/generated/NotificationDetails';
import { useReadNotificationsMutation } from '@/components/Header/components/NotificationTray/graphql/mutations/generated/readNotifications';
import useNotifications from '@/components/Header/components/NotificationTray/hooks/useNotifications';
import Loader from '@/components/Loader';
import { getCurrentTimeZoneValue } from '@/utils/timezone';

export enum ActivityCenterTabsEnum {
  NOTIFICATIONS = 'notifications',
  ACTIONS = 'actions',
}

interface ActivitiesCenterTabsProps {
  unreadNotificationsCount?: number;
  toggleTray?: () => void;
  onClickBadgeType: (notif: NotificationDetailsFragment) => void;
  onClickPointsAwarded: (notif: NotificationDetailsFragment) => void;
  showFullView?: boolean;
  currentTab: ActivityCenterTabsEnum;
  setCurrentTab: (tab: ActivityCenterTabsEnum) => void;
}

function ActivitiesCenterTabs({
  unreadNotificationsCount,
  toggleTray,
  onClickBadgeType,
  onClickPointsAwarded,
  showFullView,
  currentTab,
  setCurrentTab,
}: ActivitiesCenterTabsProps) {
  const { t } = useTranslation();
  const [, readAllNotifications] = useReadNotificationsMutation();

  // Fetch notifications data
  const { fetchMore, isFetchingMore, userNotifications, isFetching, onNotificationItemClick } =
    useNotifications(toggleTray, { userTimezone: getCurrentTimeZoneValue() });

  // Mark all notifications as read
  const readAllNotificationsRead = useCallback(() => {
    readAllNotifications({
      input: {
        readAll: true,
      },
    });
  }, [readAllNotifications]);

  // Memoized tabs configuration
  const tabs = React.useMemo(
    () => [
      {
        label: t('Common:Notifications.Header'),
        value: ActivityCenterTabsEnum.NOTIFICATIONS,
      },
      {
        label: t('Common:Actions.Actions'),
        value: ActivityCenterTabsEnum.ACTIONS,
      },
    ],
    [t],
  );

  // Memoized tab change handler
  const handleTabChange = useCallback((tab: ActivityCenterTabsEnum) => {
    setCurrentTab(tab);
  }, []);

  return (
    <Stack className="flex flex-1 flex-col" direction="col">
      {/* Tab Navigation */}
      <Stack alignItems="center" className="mb-16" direction="row" justifyContent="between">
        <Stack className="gap-8" direction="row">
          {tabs.map((tab) => (
            <StatusPill
              key={tab.value}
              appearance="secondary"
              className={cn(
                'cursor-pointer text-center',
                currentTab === tab.value
                  ? 'border border-solid !text-text-primary'
                  : 'text-text-paragraph font-normal',
              )}
              label={tab.label}
              size="large"
              variant={'light'}
              onClick={() => handleTabChange(tab.value)}
            />
          ))}
        </Stack>

        {/* Mark All as Read Button - Only show for notifications tab */}
        {currentTab === ActivityCenterTabsEnum.NOTIFICATIONS && !!userNotifications?.length && (
          <LinkButton onClick={unreadNotificationsCount ? readAllNotificationsRead : undefined}>
            <Typography
              className="label-02 xs:label-01 text-link-internal hover:text-link-internal-hover active:text-link-internal-active"
              variant="body-long-01">
              {t('Common:Notifications.MarkAllAsRead')}
              {unreadNotificationsCount ? <>&nbsp;({unreadNotificationsCount})</> : null}
            </Typography>
          </LinkButton>
        )}
      </Stack>

      {/* Tab Content */}
      {currentTab === ActivityCenterTabsEnum.NOTIFICATIONS ? (
        <Box className="flex flex-1">
          <NotificationList
            className="native:px-8"
            fetchMore={fetchMore}
            initialFetching={isFetching}
            isFetchingMore={isFetchingMore}
            userNotifications={userNotifications}
            onClickBadgeType={onClickBadgeType}
            onClickPointsAwarded={onClickPointsAwarded}
            onNotificationItemClick={onNotificationItemClick}
          />
        </Box>
      ) : (
        <Suspense
          fallback={
            <Stack alignItems="center" className="flex-1 py-12" justifyContent="center">
              <Loader />
            </Stack>
          }>
          <Box className="flex flex-1">
            <ActionsList showFullView={showFullView} />
          </Box>
        </Suspense>
      )}
    </Stack>
  );
}

export default React.memo(ActivitiesCenterTabs);
